/**
 * VAIR 9-Player Algorithm
 *
 * Based on official VAIR 9-Player Score Card v4.15.25
 *
 * Structure:
 * - 9 games total
 * - 2 courts (2 simultaneous matches per game)
 * - Each player plays 8 games (sits out 1 game as "bye")
 * - Each player partners with every other player exactly once (perfect rotation)
 * - Each player faces every other player exactly twice as opponent
 * - One player has a bye (sits out) each game
 */
import { VairRound, VairSchedule } from './types';

/**
 * Generate VAIR schedule for 9 players
 * Based on the standard VAIR score card pattern:
 * - 9 games total
 * - Each game has 2 matches (4 players per match) + 1 bye
 * - Each player plays in exactly 8 games
 */
export function generateVairSchedule9Players(timePerMatch: number = 20): VairSchedule {
  let currentTime = 0;
  const rounds: VairRound[] = [];

  // Game 1: "2-3 vs 4-7" and "5-9 vs 6-8", Player 1 Bye
  rounds.push({
    matches: [
      {
        team1: { player1: 1, player2: 2 }, // Players 2-3 (0-indexed: 1, 2)
        team2: { player1: 3, player2: 6 }, // Players 4-7 (0-indexed: 3, 6)
        court: 0,
        time: currentTime,
      },
      {
        team1: { player1: 4, player2: 8 }, // Players 5-9 (0-indexed: 4, 8)
        team2: { player1: 5, player2: 7 }, // Players 6-8 (0-indexed: 5, 7)
        court: 1,
        time: currentTime,
      },
    ],
    byes: [0], // Player 1 (0-indexed: 0)
  });

  currentTime += timePerMatch;

  // Game 2: "1-3 vs 8-5" and "6-7 vs 4-9", Player 2 Bye
  rounds.push({
    matches: [
      {
        team1: { player1: 0, player2: 2 }, // Players 1-3 (0-indexed: 0, 2)
        team2: { player1: 7, player2: 4 }, // Players 8-5 (0-indexed: 7, 4)
        court: 0,
        time: currentTime,
      },
      {
        team1: { player1: 5, player2: 6 }, // Players 6-7 (0-indexed: 5, 6)
        team2: { player1: 3, player2: 8 }, // Players 4-9 (0-indexed: 3, 8)
        court: 1,
        time: currentTime,
      },
    ],
    byes: [1], // Player 2 (0-indexed: 1)
  });

  currentTime += timePerMatch;

  // Game 3: "1-2 vs 9-6" and "4-8 vs 5-7", Player 3 Bye
  rounds.push({
    matches: [
      {
        team1: { player1: 0, player2: 1 }, // Players 1-2 (0-indexed: 0, 1)
        team2: { player1: 8, player2: 5 }, // Players 9-6 (0-indexed: 8, 5)
        court: 0,
        time: currentTime,
      },
      {
        team1: { player1: 3, player2: 7 }, // Players 4-8 (0-indexed: 3, 7)
        team2: { player1: 4, player2: 6 }, // Players 5-7 (0-indexed: 4, 6)
        court: 1,
        time: currentTime,
      },
    ],
    byes: [2], // Player 3 (0-indexed: 2)
  });

  currentTime += timePerMatch;

  // Game 4: "6-5 vs 1-7" and "3-8 vs 2-9", Player 4 Bye
  rounds.push({
    matches: [
      {
        team1: { player1: 5, player2: 4 }, // Players 6-5 (0-indexed: 5, 4)
        team2: { player1: 0, player2: 6 }, // Players 1-7 (0-indexed: 0, 6)
        court: 0,
        time: currentTime,
      },
      {
        team1: { player1: 2, player2: 7 }, // Players 3-8 (0-indexed: 2, 7)
        team2: { player1: 1, player2: 8 }, // Players 2-9 (0-indexed: 1, 8)
        court: 1,
        time: currentTime,
      },
    ],
    byes: [3], // Player 4 (0-indexed: 3)
  });

  currentTime += timePerMatch;

  // Game 5: "4-6 vs 2-8" and "1-9 vs 3-7", Player 5 Bye
  rounds.push({
    matches: [
      {
        team1: { player1: 3, player2: 5 }, // Players 4-6 (0-indexed: 3, 5)
        team2: { player1: 1, player2: 7 }, // Players 2-8 (0-indexed: 1, 7)
        court: 0,
        time: currentTime,
      },
      {
        team1: { player1: 0, player2: 8 }, // Players 1-9 (0-indexed: 0, 8)
        team2: { player1: 2, player2: 6 }, // Players 3-7 (0-indexed: 2, 6)
        court: 1,
        time: currentTime,
      },
    ],
    byes: [4], // Player 5 (0-indexed: 4)
  });

  currentTime += timePerMatch;

  // Game 6: "4-5 vs 3-9" and "2-7 vs 1-8", Player 6 Bye
  rounds.push({
    matches: [
      {
        team1: { player1: 3, player2: 4 }, // Players 4-5 (0-indexed: 3, 4)
        team2: { player1: 2, player2: 8 }, // Players 3-9 (0-indexed: 2, 8)
        court: 0,
        time: currentTime,
      },
      {
        team1: { player1: 1, player2: 6 }, // Players 2-7 (0-indexed: 1, 6)
        team2: { player1: 0, player2: 7 }, // Players 1-8 (0-indexed: 0, 7)
        court: 1,
        time: currentTime,
      },
    ],
    byes: [5], // Player 6 (0-indexed: 5)
  });

  currentTime += timePerMatch;

  // Game 7: "8-9 vs 1-4" and "2-6 vs 3-5", Player 7 Bye
  rounds.push({
    matches: [
      {
        team1: { player1: 7, player2: 8 }, // Players 8-9 (0-indexed: 7, 8)
        team2: { player1: 0, player2: 3 }, // Players 1-4 (0-indexed: 0, 3)
        court: 0,
        time: currentTime,
      },
      {
        team1: { player1: 1, player2: 5 }, // Players 2-6 (0-indexed: 1, 5)
        team2: { player1: 2, player2: 4 }, // Players 3-5 (0-indexed: 2, 4)
        court: 1,
        time: currentTime,
      },
    ],
    byes: [6], // Player 7 (0-indexed: 6)
  });

  currentTime += timePerMatch;

  // Game 8: "7-9 vs 2-5" and "3-4 vs 1-6", Player 8 Bye
  rounds.push({
    matches: [
      {
        team1: { player1: 6, player2: 8 }, // Players 7-9 (0-indexed: 6, 8)
        team2: { player1: 1, player2: 4 }, // Players 2-5 (0-indexed: 1, 4)
        court: 0,
        time: currentTime,
      },
      {
        team1: { player1: 2, player2: 3 }, // Players 3-4 (0-indexed: 2, 3)
        team2: { player1: 0, player2: 5 }, // Players 1-6 (0-indexed: 0, 5)
        court: 1,
        time: currentTime,
      },
    ],
    byes: [7], // Player 8 (0-indexed: 7)
  });

  currentTime += timePerMatch;

  // Game 9: "7-8 vs 3-6" and "4-2 vs 1-5", Player 9 Bye
  rounds.push({
    matches: [
      {
        team1: { player1: 6, player2: 7 }, // Players 7-8 (0-indexed: 6, 7)
        team2: { player1: 2, player2: 5 }, // Players 3-6 (0-indexed: 2, 5)
        court: 0,
        time: currentTime,
      },
      {
        team1: { player1: 3, player2: 1 }, // Players 4-2 (0-indexed: 3, 1)
        team2: { player1: 0, player2: 4 }, // Players 1-5 (0-indexed: 0, 4)
        court: 1,
        time: currentTime,
      },
    ],
    byes: [8], // Player 9 (0-indexed: 8)
  });

  return { rounds };
}

/**
 * Validate that the VAIR schedule is correct
 * - Each player should play in exactly (numPlayers - 1) games (for 9 players = 8 games)
 * - Each player should have exactly 1 bye
 * - No player should play with the same partner twice
 */
export function validateVairSchedule9Players(
  schedule: VairSchedule,
  numPlayers: number = 9,
): boolean {
  const playerGameCount: Map<number, number> = new Map();
  const playerByeCount: Map<number, number> = new Map();
  const playerPartners: Map<number, Set<number>> = new Map();

  // Initialize maps
  for (let i = 0; i < numPlayers; i++) {
    playerGameCount.set(i, 0);
    playerByeCount.set(i, 0);
    playerPartners.set(i, new Set());
  }

  // Track player appearances, byes, and partners
  schedule.rounds.forEach((round) => {
    // Track byes
    round.byes.forEach((player) => {
      playerByeCount.set(player, (playerByeCount.get(player) || 0) + 1);
    });

    round.matches.forEach((match) => {
      // Team 1
      const team1Players = [match.team1.player1, match.team1.player2];
      team1Players.forEach((player) => {
        playerGameCount.set(player, (playerGameCount.get(player) || 0) + 1);
        // Track partner
        const partner = team1Players.find((p) => p !== player);
        if (partner !== undefined) {
          playerPartners.get(player)?.add(partner);
        }
      });

      // Team 2
      const team2Players = [match.team2.player1, match.team2.player2];
      team2Players.forEach((player) => {
        playerGameCount.set(player, (playerGameCount.get(player) || 0) + 1);
        // Track partner
        const partner = team2Players.find((p) => p !== player);
        if (partner !== undefined) {
          playerPartners.get(player)?.add(partner);
        }
      });
    });
  });

  // Validate: Each player should play in (numPlayers - 1) games
  const expectedGames = numPlayers - 1;
  for (let i = 0; i < numPlayers; i++) {
    const gamesPlayed = playerGameCount.get(i) || 0;
    if (gamesPlayed !== expectedGames) {
      console.error(`Player ${i + 1} played ${gamesPlayed} games, expected ${expectedGames}`);
      return false;
    }
  }

  // Validate: Each player should have exactly 1 bye
  for (let i = 0; i < numPlayers; i++) {
    const byeCount = playerByeCount.get(i) || 0;
    if (byeCount !== 1) {
      console.error(`Player ${i + 1} had ${byeCount} byes, expected 1`);
      return false;
    }
  }

  return true;
}
